import { z } from "zod";
import { budgetTypeList } from "@/types";

const part1Schema = z
  .object({
    agreesWithScope: z.boolean({
      required_error: "Agreeing or disagreeing with scope is required",
    }),
    agreesWithPay: z.boolean({
      required_error: "Agreeing or disagreeing with pay is required",
    }),
    agreesWithStartDate: z.boolean({
      required_error: "Agreeing or disagreeing with the start date is required",
    }),
    scope: z.string().min(1, "Proposed scope is required").optional(),
    budgetType: z.enum(budgetTypeList).optional(),
    budgetHourly: z.string().min(1, "Proposed budget is required").optional(),
    budgetMonthly: z.string().min(1, "Proposed budget is required").optional(),
    budgetMinCents: z
      .number()
      .int()
      .min(1, { message: "A minimum budget is required" })
      .nullable()
      .refine((v) => v !== null, { message: "A minimum budget is required" })
      .optional(),
    budgetMaxCents: z
      .number()
      .int()
      .min(1, { message: "A maximum budget is required" })
      .nullable()
      .refine((v) => v !== null, { message: "A maximum budget is required" })
      .optional(),
    earliestStartDate: z
      .date({
        required_error: "Ideal start date is required",
      })
      .optional(),
  })
  .refine((input) => input.agreesWithScope || input.scope, {
    message: "You must accept the proposed scope or provide an alternative",
    path: ["agreesWithScope"],
  })
  .refine((input) => input.agreesWithStartDate || input.earliestStartDate, {
    message: "You must accept the proposed scope or provide an alternative",
    path: ["agreesWithStartDate"],
  })
  .refine(
    (input) =>
      // Must either:
      // - Agree with the proposed budget
      // - Supply an alternative hourly and monthly budget (used for retainer)
      // - Supply a monthly budget and a type (used for projects)
      // - Supply a budget range (used for full-time roles)
      input.agreesWithPay ||
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      (input.budgetHourly && input.budgetMonthly) ||
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      (input.budgetType && input.budgetMonthly) ||
      (input.budgetMinCents && input.budgetMaxCents),
    {
      message: "You must accept the proposed pay or provide an alternative",
      path: ["agreesWithPay"],
    },
  )
  .refine(
    (input) =>
      !input.budgetMinCents ||
      !input.budgetMaxCents ||
      input.budgetMinCents <= input.budgetMaxCents,
    {
      message: "Maximum budget cannot be less than minimum budget",
      path: ["budgetMaxCents"],
    },
  );

const part2Schema = z.object({
  matchCriteria1Quote: z
    .string()
    .min(1, "Match criteria quote is required")
    .max(300, "Please limit your suggestion to 300 characters or less"),
  matchCriteria2Quote: z
    .string()
    .min(1, "Match criteria quote is required")
    .max(300, "Please limit your suggestion to 300 characters or less"),
  matchCriteria3Quote: z
    .string()
    .min(1, "Match criteria quote is required")
    .max(300, "Please limit your suggestion to 300 characters or less"),
  matchCriteria1Suggestion: z
    .string()
    .min(1, "Match criteria change suggestion is required")
    .max(300, "Please limit your suggestion to 300 characters or less")
    .optional(),
  matchCriteria2Suggestion: z
    .string()
    .min(1, "Match criteria change suggestion is required")
    .max(300, "Please limit your suggestion to 300 characters or less")
    .optional(),
  matchCriteria3Suggestion: z
    .string()
    .min(1, "Match criteria change suggestion is required")
    .max(300, "Please limit your suggestion to 300 characters or less")
    .optional(),
});

type Part1Schema = z.infer<typeof part1Schema>;
type Part2Schema = z.infer<typeof part2Schema>;

export {
  part1Schema as applyOpportunityFormPart1Schema,
  type Part1Schema as ApplyOpportunityFormPart1Schema,
  part2Schema as applyOpportunityFormPart2Schema,
  type Part2Schema as ApplyOpportunityFormPart2Schema,
};
